import {
  BoxedDeliveryIcon,
  BoxedFingerIcon,
  BoxedRingIcon,
  BoxedRingOr4Icon,
  BoxedSizeKitIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import RichText from '@/app/components/RichText';
import useDisplayBestBuy from '@/app/hooks/useDisplayBestBuy';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { tValueBestBuy, tValueSizing } from './tValues';

interface ProductSizingModalProps {
  readonly onSizeLinkClick: () => void;
  readonly isOuraRing4Wizard: boolean;
}

const DesktopSizeModal = ({
  onSizeLinkClick,
  isOuraRing4Wizard,
}: ProductSizingModalProps) => {
  const isBestBuyCountry = useDisplayBestBuy();
  const isBestBuyLinkActive = useFeatureFlag('best-buy-store-locator-page');
  const t = useTranslations();

  return (
    <section className="text-sandstone-500">
      <TypographyV3
        Element="h2"
        variant="h2"
        color="inherit"
        className="pt-5 text-5xl"
      >
        {t('pdp_horizon_sizing_modal_title')}
      </TypographyV3>
      <TypographyV3
        Element="h3"
        variant="h4"
        color="inherit"
        className="py-7 text-base font-normal"
      >
        {t('pdp_size_modal_choose_the_right')}
      </TypographyV3>
      <TypographyV3
        Element="h4"
        variant="h3"
        color="inherit"
        className="border-t border-gray-400 pt-7 text-4xl font-light"
      >
        {t('pdp_size_modal_size_at_home')}
      </TypographyV3>

      <div className="grid grid-cols-2 gap-7 lg:grid-cols-4">
        <div className="col-start-1 col-end-2 py-7">
          <BoxedSizeKitIcon />
          <TypographyV3
            Element="h5"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_1_title')}
          </TypographyV3>
          <TypographyV3
            Element="p"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_1_content')}
          </TypographyV3>
        </div>

        <div className="col-start-2 col-end-3 py-7">
          <BoxedDeliveryIcon />
          <TypographyV3
            Element="h5"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_2_title')}
          </TypographyV3>
          <TypographyV3
            Element="p"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_2_content_04_2025')}
          </TypographyV3>
        </div>

        <div className="col-start-1 col-end-2 py-7 lg:col-start-3 lg:col-end-4">
          <BoxedFingerIcon />
          <TypographyV3
            Element="h5"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_3_title')}
          </TypographyV3>
          <TypographyV3
            Element="p"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_3_content')}
          </TypographyV3>
        </div>

        <div
          className="col-start-2 col-end-3 py-7 lg:col-start-4 lg:col-end-5"
          data-cy={isOuraRing4Wizard ? 'boxed-oreo-svg' : 'boxed-svg'}
        >
          {isOuraRing4Wizard ? <BoxedRingOr4Icon /> : <BoxedRingIcon />}
          <TypographyV3
            Element="h5"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_4_title')}
          </TypographyV3>
          <TypographyV3
            Element="p"
            variant="h4"
            color="inherit"
            className="pt-5 text-base font-normal"
          >
            {t('pdp_size_modal_step_4_content')}
          </TypographyV3>
        </div>
      </div>

      {isBestBuyLinkActive && isBestBuyCountry && (
        <>
          <TypographyV3
            Element="h4"
            variant="h3"
            color="inherit"
            className="border-t border-gray-400 pt-7 text-4xl font-light"
          >
            <RichText messageKey="pdp_size_modal_size_in_person_best_buy_title" />
          </TypographyV3>
          <TypographyV3
            Element="p"
            variant="h4"
            color="inherit"
            className="py-7 text-base font-normal"
          >
            <RichText
              messageKey="pdp_size_modal_size_in_person_best_buy_content"
              tValues={tValueBestBuy}
            />
          </TypographyV3>
        </>
      )}
      <TypographyV3
        Element="p"
        variant="h4"
        color="inherit"
        className="border-t border-gray-400 py-7 text-base font-normal"
      >
        <RichText
          messageKey="pdp_size_modal_size_video_text_link"
          tValues={tValueSizing(onSizeLinkClick)}
        />
      </TypographyV3>
    </section>
  );
};
export default DesktopSizeModal;
